import filter from 'lodash/filter'
import reduce from 'lodash/reduce'
import getConfig from 'next/config'
import React, { createContext, FC, ReactNode, useCallback, useMemo } from 'react'

import { Breadcrumb } from '@Components/Breadcrumbs/breadcrumb'
import { Category, Field, Page } from '@Generated/graphql'
import { Money } from '@Globals/Money'

export interface AvailableLocale {
  localeCode: string
  title: string
  url: string
}

export interface AlternateLink {
  locale: string
  url: string
}

export interface PageObject {
  breadcrumbs?: Breadcrumb[]
  id: string
  type: string
  url: string
}

export interface CMSURLs {
  aboutUs: string
  cookiePolicy: string
  csr: string
  privacyPolicy: string
  terms: string
}

export interface ExtraURL {
  content: string
  href: string
}

export interface CMSData {
  latestArticle: Page | null
  latestCollections: Page[]
  urls: CMSURLs
}

export interface InfoURLs {
  delivery: string
  paymentAndSecurity: string
  returns: string
}

export interface InfoData {
  extraURLs: ExtraURL[]
  urls: InfoURLs
}

export interface PaymentMethod {
  icons: PaymentIcon[]
  provider: string
  type: string
}

export interface PaymentIcon {
  name: string
  url: string
}

type RedirectType = 'category' | 'cms' | 'product'

export interface StoreConfig {
  adwordsCurrencyRatesMap: Array<[string, number]>
  adwordsTrackingID: string
  analyticsTrackingID: string
  cheapestShippingPrice?: Money
  countryCode: string
  currencyCode: string
  email: string
  freeShippingThreshold?: Money
  giftcards?: {
    predefinedValues: Array<Money>
    valueMax: Money
    valueMin: Money
  }
  localeCode: string
  merchantAccount: string
  phone: string
  trustpilotBusinessID: string
  trustpilotDomain: string
  urlDomain: string
  urlFull: string
  urlPath: string
  usercentricsEnabled: boolean
  vatNumber: string
}

export interface Store {
  config?: StoreConfig
  id: string
  mainStore: string
  title: string
}

export interface RTEField {
  key: string
  references: []
  value: string
}

export interface Bar {
  backgroundColor: string
  fields: Field[]
  id: number
  placement: string[]
  priority: number
  textColor: string
}

interface Metadata {
  addSurrogateKey: (key: string) => void
  alternateLinks: AlternateLink[]
  availableLocales: AvailableLocale[]
  bars: Bar[]
  categories: Category[]
  categoriesMenu: Category[]
  cmsData: CMSData
  infoData: InfoData
  page?: PageObject
  paymentMethods: PaymentMethod[]
  redirectURL: (type: RedirectType, id: string, useStore?: boolean) => string
  store: Store
  storeURL: (path: string, params?: URLSearchParams, useOrigin?: boolean) => string
}

interface MetadataProviderProps {
  addSurrogateKey: (key: string) => void
  alternateLinks: AlternateLink[]
  availableLocales: AvailableLocale[]
  bars: Bar[]
  categories: Category[]
  children: ReactNode
  cmsData: CMSData
  infoData: InfoData
  isLocal: boolean
  page?: PageObject
  paymentMethods: PaymentMethod[]
  store: Store
}

const nestCategories = (categories: Category[]): Category[] => reduce(
  categories,
  (memo, category) => {
    if (category.parentID) {
      return memo
    }

    return [
      ...memo,
      {
        ...category,
        children: filter(categories, ['parentID', category.id]),
      },
    ]
  },
  [],
)

export const MetadataContext = createContext<Metadata>({} as Metadata)
export const MetadataConsumer = MetadataContext.Consumer
export const MetadataProvider: FC<MetadataProviderProps> = (
  {
    addSurrogateKey,
    alternateLinks,
    availableLocales,
    bars,
    categories,
    children,
    cmsData,
    infoData,
    isLocal,
    page,
    paymentMethods,
    store,
  }: MetadataProviderProps,
) => {
  const categoriesMenu = useMemo(
    () => {
      const excludedCategories = ['27', '28', '69', '174', '223', '224']
      return nestCategories(categories).filter(category => !excludedCategories.some(x => x === category.id))
    },
    [categories],
  )

  const storeURL = useCallback((path: string, params?: URLSearchParams, useOrigin?: boolean) => {
    if (!store?.config?.urlFull) {
      return path + (params ? `?${params.toString()}` : '')
    }

    const base = useOrigin ? new URL(store.config.urlFull).origin : store.config.urlFull
    const url = new URL(path, isLocal ? 'http://localhost:3000/' : base)

    if (params) {
      params.forEach((value, key) => {
        url.searchParams.set(key, value)
      })
    }

    return url.toString()
  }, [isLocal, store?.config?.urlFull])

  const { publicRuntimeConfig } = getConfig()

  const redirectURL = useCallback((
    type: RedirectType,
    id: string,
    useStore: boolean = false,
  ) => {
    const url = new URL(`${publicRuntimeConfig.redirectEndpoint}/redirect/type/${type}/id/${id}`)

    if (useStore) {
      url.searchParams.set('store', store?.id)
    }

    return url.toString()
  }, [publicRuntimeConfig.redirectEndpoint, store?.id])

  return (
    <MetadataContext.Provider
      // TODO: avoid rerendering when no metadata changes
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        addSurrogateKey,
        alternateLinks,
        availableLocales,
        bars,
        categories,
        categoriesMenu,
        cmsData,
        infoData,
        page,
        paymentMethods,
        redirectURL,
        store,
        storeURL,
      }}
    >
      {children}
    </MetadataContext.Provider>
  )
}
